import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { AlertCircle, CheckCircle2, Mail, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { isValidEmail, normalizeEmail } from '../utils/authForms';

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(searchParams.get('role') || 'client');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    const cleanEmail = normalizeEmail(email);
    if (!isValidEmail(cleanEmail)) {
      setError('Ingresa un correo valido.');
      return;
    }
    setEmail(cleanEmail);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, {
        email: cleanEmail,
        expected_role: role
      });
      setMessage(data.message);
    } catch (err) {
      setError(!err.response ? 'No se pudo conectar con el servidor.' : 'No se pudo procesar la solicitud.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="mesh-glow-pink" style={{ top: '10%', left: '0%' }} />
      <div className="w-full max-w-md relative z-10">
        <div className="glass-card p-8 rounded-3xl">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <Sparkles className="text-[#FF007F] w-8 h-8" />
            <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>PROVIDER</h1>
          </div>
          <h2 className="text-3xl font-medium text-white mb-2 text-center" style={{ fontFamily: 'Outfit' }}>Recuperar contrasena</h2>
          <p className="text-sm text-[#9CA3AF] text-center mb-8">Ingresa tu correo y enviaremos instrucciones.</p>
          {message && <Notice type="success" text={message} />}
          {error && <Notice type="error" text={error} />}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-white mb-2 block">Correo electronico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-11 bg-white/5 border-white/10 rounded-xl text-white"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="role" className="text-white mb-2 block">Tipo de cuenta</Label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-4 py-3 bg-[#111217] border border-white/10 rounded-xl text-white"
              >
                <option value="client">Cliente</option>
                <option value="provider">Proveedor</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <Button type="submit" disabled={loading} className="w-full gradient-button py-6 rounded-xl">{loading ? 'Enviando...' : 'Enviar instrucciones'}</Button>
          </form>
          <Link to="/login" className="block text-center text-sm text-[#9CA3AF] hover:text-white mt-6">Volver a login</Link>
        </div>
      </div>
    </div>
  );
};

const Notice = ({ type, text }) => (
  <div className={`mb-5 p-4 rounded-xl border flex gap-3 ${type === 'success' ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
    {type === 'success' ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <AlertCircle className="w-5 h-5 text-red-400" />}
    <p className={`text-sm ${type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{text}</p>
  </div>
);

export default ForgotPasswordPage;
